import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  createFoodInvoice,
  createFoodVendor,
  extractFoodInvoice,
  fetchFoodIngredients,
  fetchFoodVendors,
  suggestFoodInvoiceMappings,
  uploadFoodInvoiceFile,
  type FoodIngredient,
  type FoodMappingSuggestion,
  type FoodVendor,
} from "../foodApi";
import { COMMON_UNITS, money } from "../utils/foodFormatters";
import {
  INPUT_CLASS,
  SMALL_BUTTON_CLASS,
  costPerUnitLabel,
  parseLineDraft,
  sumLineTotals,
  toLineDraft,
  totalsDiffer,
  type LineDraft,
  type LineValues,
} from "../utils/invoiceLines";
import { AddIngredientModal } from "../components/AddIngredientModal";

type ReviewRow = LineDraft & {
  excluded: boolean;
  matchType?: FoodMappingSuggestion["matchType"];
  confidence?: number;
};

type ReviewHeader = {
  invoiceNumber: string;
  invoiceDate: string;
  /** The invoice's printed total; blank means "use the sum of included lines". */
  totalAmount: string;
};

/** The common units, plus whatever unit the line already has so it stays selectable. */
function unitOptions(current: string): string[] {
  const units: string[] = [...COMMON_UNITS];
  return current && !units.includes(current) ? [current, ...units] : units;
}

function findVendor(vendors: FoodVendor[], name: string | null | undefined) {
  if (!name) return undefined;
  return vendors.find((v) => v.name.toLowerCase() === name.trim().toLowerCase());
}

export function UploadInvoiceView() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<"idle" | "uploading" | "extracting" | "review">("idle");
  const [error, setError] = useState<string | null>(null);
  const [extractedVendorName, setExtractedVendorName] = useState<string | null>(null);
  const [header, setHeader] = useState<ReviewHeader | null>(null);
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [uploadedPath, setUploadedPath] = useState<string | null>(null);
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [addIngredientForIndex, setAddIngredientForIndex] = useState<number | null>(null);

  function updateRow(index: number, patch: Partial<ReviewRow>) {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  const vendorsQuery = useQuery({ queryKey: ["food", "vendors"], queryFn: fetchFoodVendors });
  const ingredientsQuery = useQuery({
    queryKey: ["food", "ingredients"],
    queryFn: fetchFoodIngredients,
  });
  const vendorOptions: FoodVendor[] = vendorsQuery.data ?? [];

  const uploadMutation = useMutation({
    mutationFn: async (selected: File) => {
      setError(null);
      setStep("uploading");
      const up = await uploadFoodInvoiceFile(selected);
      setUploadedPath(up.filePath);
      setStep("extracting");
      const parsed = await extractFoodInvoice(up.filePath);
      setExtractedVendorName(parsed.vendorName);
      setHeader({
        invoiceNumber: parsed.invoiceNumber ?? "",
        invoiceDate: parsed.invoiceDate,
        totalAmount: parsed.totalAmount == null ? "" : String(parsed.totalAmount),
      });
      setRows(
        parsed.lineItems.map((li) => ({ ...toLineDraft(li), excluded: Boolean(li.excluded) })),
      );
      setVendorId(findVendor(vendorOptions, parsed.vendorName)?.id ?? null);
      setStep("review");
      return parsed;
    },
    onError: (e) => {
      setError((e as Error).message);
      setStep("idle");
    },
  });

  const includedRows = rows.filter((row) => !row.excluded);
  const linesTotal = sumLineTotals(includedRows);
  const invoiceTotalInput = header?.totalAmount.trim() ? Number(header.totalAmount) : null;
  const invoiceTotal =
    invoiceTotalInput !== null && Number.isFinite(invoiceTotalInput) ? invoiceTotalInput : null;

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!header || !uploadedPath || !vendorId) throw new Error("Choose a vendor first");
      if (includedRows.length === 0) throw new Error("Include at least one line");
      const lineItems: LineValues[] = [];
      for (const row of includedRows) {
        const parsed = parseLineDraft(row);
        if (!parsed.ok) throw new Error(parsed.error);
        lineItems.push(parsed.value);
      }
      if (header.totalAmount.trim() && invoiceTotal === null) {
        throw new Error("Invoice total must be a number");
      }
      return createFoodInvoice({
        vendorId,
        invoiceNumber: header.invoiceNumber.trim() || null,
        invoiceDate: header.invoiceDate,
        totalAmount: invoiceTotal ?? linesTotal,
        filePath: uploadedPath,
        lineItems,
      });
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["food"] });
      navigate(`/food/invoices/${saved.id}`);
    },
    onError: (e) => setError((e as Error).message),
  });

  const suggestMutation = useMutation({
    mutationFn: async () => {
      if (!vendorId || rows.length === 0) return;
      const { suggestions } = await suggestFoodInvoiceMappings(
        vendorId,
        rows.map((row) => row.description),
        rows.map((row) => ({
          unitCost: Number(row.unitCost) || 0,
          totalCost: Number(row.totalCost) || 0,
        })),
      );
      setRows((prev) =>
        prev.map((row, index) => {
          const suggestion = suggestions.find((s) => s.index === index);
          if (!suggestion) return row;
          return {
            ...row,
            ingredientId: suggestion.ingredientId,
            quantity: String(suggestion.quantity),
            unit: suggestion.unit,
            matchType: suggestion.matchType,
            confidence: suggestion.confidence,
          };
        }),
      );
    },
    onError: (e) => setError((e as Error).message),
  });

  const createVendorMutation = useMutation({
    mutationFn: async () => {
      if (!extractedVendorName) return;
      const existing = findVendor(vendorOptions, extractedVendorName);
      const vendor = existing ?? (await createFoodVendor(extractedVendorName.trim()));
      setVendorId(vendor.id);
      if (!existing) queryClient.invalidateQueries({ queryKey: ["food", "vendors"] });
    },
    onError: (e) => setError((e as Error).message),
  });

  // Archived ingredients stay listed only when a line already uses them.
  const selectedIngredientIds = new Set(rows.map((row) => row.ingredientId));
  const ingredientOptions = (ingredientsQuery.data ?? []).filter(
    (opt) => opt.isActive || selectedIngredientIds.has(opt.id),
  );
  const extractedVendorMissing =
    Boolean(extractedVendorName) && !findVendor(vendorOptions, extractedVendorName);
  const excludedCount = rows.length - includedRows.length;

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
        <h2 className="text-base font-semibold">Upload invoice</h2>
        <p className="mt-1 text-sm text-tribal-muted">
          Upload a PDF, photo, or CSV/XLSX export, check the lines, then save.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="min-w-0 text-sm text-tribal-muted"
          />
          <button
            type="button"
            disabled={!file || uploadMutation.isPending}
            onClick={() => file && uploadMutation.mutate(file)}
            className="rounded-md bg-tribal-red px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
          >
            {step === "uploading" || step === "extracting" ? "Processing..." : "Upload & Extract"}
          </button>
        </div>
        {error && <p className="mt-3 text-sm text-red-300">{error}</p>}
      </section>

      {header && (
        <section className="space-y-3 rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">Review extracted invoice</h3>
            <button
              type="button"
              disabled={!vendorId || suggestMutation.isPending}
              onClick={() => suggestMutation.mutate()}
              className={SMALL_BUTTON_CLASS}
            >
              {suggestMutation.isPending ? "Suggesting..." : "Apply historical mappings"}
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="text-sm">
              <div className="mb-1 text-xs uppercase tracking-wide text-tribal-muted">Vendor</div>
              <select
                value={vendorId ?? ""}
                onChange={(e) => setVendorId(e.target.value ? Number(e.target.value) : null)}
                className={`w-full ${INPUT_CLASS}`}
              >
                <option value="">Select vendor</option>
                {vendorOptions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
              {extractedVendorMissing && (
                <button
                  type="button"
                  onClick={() => createVendorMutation.mutate()}
                  disabled={createVendorMutation.isPending}
                  className={`mt-1 ${SMALL_BUTTON_CLASS}`}
                >
                  Add vendor “{extractedVendorName}”
                </button>
              )}
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs uppercase tracking-wide text-tribal-muted">Invoice #</div>
              <input
                value={header.invoiceNumber}
                onChange={(e) => setHeader({ ...header, invoiceNumber: e.target.value })}
                placeholder="Optional"
                className={`w-full ${INPUT_CLASS}`}
              />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs uppercase tracking-wide text-tribal-muted">
                Invoice Date
              </div>
              <input
                type="date"
                value={header.invoiceDate}
                onChange={(e) => setHeader({ ...header, invoiceDate: e.target.value })}
                className={`w-full ${INPUT_CLASS}`}
              />
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs uppercase tracking-wide text-tribal-muted">
                Invoice Total ($)
              </div>
              <input
                inputMode="decimal"
                value={header.totalAmount}
                onChange={(e) => setHeader({ ...header, totalAmount: e.target.value })}
                placeholder={linesTotal.toFixed(2)}
                className={`w-full ${INPUT_CLASS}`}
              />
            </label>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-tribal-muted">
                <tr className="border-b border-tribal-border">
                  <th className="px-2 py-2">Include</th>
                  <th className="px-2 py-2">Description</th>
                  <th className="px-2 py-2">Quantity</th>
                  <th className="px-2 py-2" title="What the vendor charged per billed unit">
                    Price
                  </th>
                  <th className="px-2 py-2">Total</th>
                  <th className="px-2 py-2" title="Line total ÷ quantity">
                    Cost / unit
                  </th>
                  <th className="px-2 py-2">Ingredient Mapping</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tribal-border/60">
                {rows.map((row, index) => (
                  <tr
                    key={index}
                    className={`align-top ${row.excluded ? "opacity-50" : ""}`}
                  >
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Include ${row.description}`}
                        checked={!row.excluded}
                        onChange={(e) => updateRow(index, { excluded: !e.target.checked })}
                        className="mt-2"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        aria-label="Description"
                        value={row.description}
                        disabled={row.excluded}
                        onChange={(e) => updateRow(index, { description: e.target.value })}
                        className={`w-56 ${INPUT_CLASS}`}
                      />
                      {row.excluded && (
                        <div className="mt-1 text-xs text-tribal-muted">Excluded, won't be saved</div>
                      )}
                      {row.matchType && !row.excluded && (
                        <div className="mt-1 text-xs text-tribal-muted">
                          {row.matchType} match ({Math.round((row.confidence ?? 0) * 100)}%)
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1.5">
                        <input
                          inputMode="decimal"
                          aria-label={`Quantity for ${row.description}`}
                          value={row.quantity}
                          disabled={row.excluded}
                          onChange={(e) => updateRow(index, { quantity: e.target.value })}
                          className={`w-20 ${INPUT_CLASS}`}
                        />
                        <select
                          aria-label={`Unit for ${row.description}`}
                          value={row.unit}
                          disabled={row.excluded}
                          onChange={(e) => updateRow(index, { unit: e.target.value })}
                          className={INPUT_CLASS}
                        >
                          {unitOptions(row.unit).map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <input
                        inputMode="decimal"
                        aria-label={`Price for ${row.description}`}
                        value={row.unitCost}
                        disabled={row.excluded}
                        onChange={(e) => updateRow(index, { unitCost: e.target.value })}
                        className={`w-24 ${INPUT_CLASS}`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        inputMode="decimal"
                        aria-label={`Total for ${row.description}`}
                        value={row.totalCost}
                        disabled={row.excluded}
                        onChange={(e) => updateRow(index, { totalCost: e.target.value })}
                        className={`w-24 ${INPUT_CLASS}`}
                      />
                    </td>
                    <td className="whitespace-nowrap px-2 py-3 text-tribal-muted">
                      {costPerUnitLabel(row)}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1.5">
                        <select
                          value={row.ingredientId ?? ""}
                          disabled={row.excluded}
                          onChange={(e) =>
                            updateRow(index, {
                              ingredientId: e.target.value ? Number(e.target.value) : null,
                            })
                          }
                          className={`w-52 ${INPUT_CLASS}`}
                        >
                          <option value="">Unmapped</option>
                          {ingredientOptions.map((ingredient) => (
                            <option key={ingredient.id} value={ingredient.id}>
                              {ingredient.name}
                              {!ingredient.isActive ? " (archived)" : ""}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          title="Create new ingredient"
                          disabled={row.excluded}
                          onClick={() => setAddIngredientForIndex(index)}
                          className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-md border border-tribal-border bg-black/20 text-tribal-muted transition-colors hover:border-tribal-red hover:text-white disabled:opacity-60"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-1 text-sm">
            <p>
              {includedRows.length} line{includedRows.length === 1 ? "" : "s"} included
              {excludedCount > 0 && `, ${excludedCount} excluded`} · Lines total{" "}
              <span className="font-semibold">{money(linesTotal)}</span>
              {invoiceTotal !== null && (
                <>
                  {" "}· Invoice total <span className="font-semibold">{money(invoiceTotal)}</span>
                </>
              )}
            </p>
            {totalsDiffer(linesTotal, invoiceTotal) && (
              <p className="text-amber-400">
                The included lines don't add up to the invoice total. Check for lines to exclude
                (tax, totals, payments) or a wrong line total.
              </p>
            )}
          </div>

          <button
            type="button"
            disabled={!vendorId || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
            className="rounded-md bg-tribal-red px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
          >
            {saveMutation.isPending ? "Saving..." : "Save Invoice"}
          </button>
        </section>
      )}

      {addIngredientForIndex !== null && (
        <AddIngredientModal
          defaultName={rows[addIngredientForIndex]?.description ?? ""}
          onClose={() => setAddIngredientForIndex(null)}
          onCreated={(created: FoodIngredient) => {
            const idx = addIngredientForIndex;
            setRows((prev) =>
              prev.map((row, i) => (i === idx ? { ...row, ingredientId: created.id } : row)),
            );
          }}
        />
      )}
    </div>
  );
}
